/**
 * ReviewCTA — Post-delivery review request + Cinco cross-sell
 *
 * Shown after order is delivered (status: Teruggebracht).
 * Two sections:
 * 1. Review request — Google Reviews + Klantenvertellen
 * 2. Cross-sell — Bankreiniging via Cinco platform
 */

import { useState } from 'react'
import { Card, Icon } from './UI'
import { trackEvent } from '../analytics'

// =============================================================================
// REVIEW CTA
// =============================================================================

interface ReviewCTAProps {
  werkOrderId: string
  klantNaam?: string
}

const REVIEW_LINKS = {
  google: 'https://g.page/r/CXFNAmomooQwEAE/review',
  klantenvertellen: 'https://www.klantenvertellen.nl/invite-link/1039504/cinco_cleaning?lang=nl',
}

export function ReviewCTA({ werkOrderId, klantNaam }: ReviewCTAProps) {
  const [hoveredStar, setHoveredStar] = useState(0)

  function handleReviewClick(platform: 'google' | 'klantenvertellen') {
    trackEvent('review_clicked', { werkOrderId, reviewPlatform: platform })
    window.open(REVIEW_LINKS[platform], '_blank', 'noopener')
  }

  return (
    <div className="space-y-md">
      {/* Review Request */}
      <Card className="overflow-hidden">
        <div className="text-center py-sm">
          <h2 className="text-heading-sub font-bold text-ink mb-xs">
            {klantNaam ? `${klantNaam}, hoe was je ervaring?` : 'Hoe was je ervaring?'}
          </h2>
          <p className="text-body-sm text-ink-muted mb-md">
            Met een korte review help je ons en andere kleedliefhebbers. Het
            kost je een minuutje.
          </p>

          {/* Star rating — visual prompt, clicks through to Google */}
          <div className="flex justify-center gap-xs mb-md">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(0)}
                onClick={() => handleReviewClick('google')}
                className="text-3xl transition-transform hover:scale-110"
                aria-label={`${star} sterren`}
              >
                <span className={hoveredStar >= star ? 'opacity-100' : 'opacity-30'}>
                  &#9733;
                </span>
              </button>
            ))}
          </div>

          {/* Primary CTA — Google */}
          <button
            type="button"
            onClick={() => handleReviewClick('google')}
            className="btn-primary w-full py-md text-body font-medium mb-sm"
          >
            Plaats een Google review
          </button>

          {/* Secondary — Klantenvertellen */}
          <button
            type="button"
            onClick={() => handleReviewClick('klantenvertellen')}
            className="w-full text-center text-body-sm text-primary hover:text-primary-dark font-medium py-xs transition-colors"
          >
            Of beoordeel ons op Klantenvertellen
          </button>
        </div>
      </Card>

      {/* Cinco Cross-sell — Bankreiniging */}
      <CincoCrossSell werkOrderId={werkOrderId} />
    </div>
  )
}

// =============================================================================
// CINCO CROSS-SELL
// =============================================================================

interface CincoCrossSellProps {
  werkOrderId: string
}

function CincoCrossSell({ werkOrderId }: CincoCrossSellProps) {
  function handleClick() {
    trackEvent('crosssell_clicked', { werkOrderId, crosssellType: 'bankreiniging' })
    window.open('https://www.cincocleaning.com/bank-laten-reinigen', '_blank', 'noopener')
  }

  return (
    <Card>
      <div className="flex items-start gap-md">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="sparkles" className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-body font-medium text-ink mb-xs">
            Ook je bank laten reinigen?
          </p>
          <p className="text-body-sm text-ink-muted mb-sm">
            Via ons zustermerk Cinco Cleaning vind je een vakspecialist die je
            bank bij je thuis reinigt.
          </p>
          <button
            type="button"
            onClick={handleClick}
            className="text-body-sm text-primary hover:text-primary-dark font-medium transition-colors"
          >
            Bekijk bankreiniging &rarr;
          </button>
        </div>
      </div>
    </Card>
  )
}
